/**
 * Labs Order Interceptor
 *
 * Detects when user reaches labs_order_status question and redirects to labs flow.
 * Checks lab orders API to determine if user has already completed ordering.
 * Creates person record from questionnaire answers before navigation to enable form autopopulation.
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { InterceptorProps } from './types';
import { ROUTE_LABS_ORDER_STEP_1 } from '@/constants/routes';
import { useListLabOrdersV1LabsOrdersGet } from '@/api/generated-catch-backend/laboratory-orders/laboratory-orders';
import { OrderStatus } from '@/api/generated-catch-backend/model';
import { useGetQuestionWithNavigation } from '@/app/services/questionnaireV2.service';
import { useCreatePersonFromQuestionnaire } from '@/app/services/persons.service';

export function LabsOrderInterceptor({ context, onComplete }: InterceptorProps) {
  const { questionnaireId, responseId, questionSlug } = context;
  const navigate = useNavigate();
  const hasNavigated = useRef(false);
  const { mutateAsync: createPerson } = useCreatePersonFromQuestionnaire();

  console.log('[LabsOrderInterceptor] Triggered for question:', questionSlug);

  // Fetch navigation context for this question to get previousQuestionSlug
  const { data: navigationContext, isLoading: isLoadingNavigation } = useGetQuestionWithNavigation(
    questionnaireId,
    responseId,
    questionSlug,
    true // Always fetch
  );

  // Query lab orders API to check if user has ordered
  const { data: labOrders, isLoading: isLoadingLabOrders } = useListLabOrdersV1LabsOrdersGet({
    query: {
      staleTime: 0, // Always fetch fresh data
      refetchOnMount: true,
    }
  });

  const isLoading = isLoadingNavigation || isLoadingLabOrders;

  // Check if user has a successfully created order (requisition created or beyond)
  // Exclude: cancelled, failed, and early-stage orders that didn't complete
  const successfulStatuses: OrderStatus[] = [
    OrderStatus.requisition_created,
    OrderStatus.sample_collected,
    OrderStatus.sample_with_lab,
    OrderStatus.partial_results,
    OrderStatus.completed,
    OrderStatus.processed,
  ];

  const hasLabOrder = labOrders?.data?.some(
    order => successfulStatuses.includes(order.status)
  );

  useEffect(() => {
    // Wait for data to load
    if (isLoading) {
      return;
    }

    // If user has a lab order, they already completed the flow - submit synthetic answer
    if (hasLabOrder) {
      console.log('[LabsOrderInterceptor] User has lab order, submitting success answer');
      onComplete({
        answerValue: 'labs_ordered_successfully',
        shouldContinue: true,
        metadata: { timestamp: Date.now() }
      });
      return;
    }

    // No lab order - navigate to labs flow (one-way, never return here)
    if (!hasNavigated.current) {
      hasNavigated.current = true;
      console.log('[LabsOrderInterceptor] No lab order found, navigating to labs flow');

      // Create person from questionnaire answers before navigating
      // This enables lab order form to autopopulate with demographic data
      const navigateToLabsFlow = async () => {
        try {
          console.log('[LabsOrderInterceptor] Creating person from questionnaire answers');
          await createPerson({ questionnaire_response_id: responseId });
          console.log('[LabsOrderInterceptor] Person created successfully');
        } catch (error) {
          // Don't block navigation on person creation failure
          // Person will be created later (by risk calc or lab order service)
          console.warn('[LabsOrderInterceptor] Failed to create person, continuing to labs flow:', error);
        }

        const queryParams = new URLSearchParams({
          source: 'risk_assessment',
          questionnaire_id: questionnaireId,
          response_id: responseId,
        });

        // Include previous question slug if available for back button navigation
        const previousQuestionSlug = navigationContext?.previousQuestionSlug;
        if (previousQuestionSlug) {
          queryParams.set('previous_question_slug', previousQuestionSlug);
        }

        navigate(`${ROUTE_LABS_ORDER_STEP_1}?${queryParams.toString()}`);
      };

      navigateToLabsFlow();
    }
  }, [hasLabOrder, isLoading, onComplete, questionnaireId, responseId, navigate, navigationContext, createPerson]);

  // Show nothing while loading or navigating
  return null;
}
